import { Group, Text, useMantineTheme, Tooltip, Badge } from '@mantine/core';
import {
  IconPlus,
  IconEdit,
  IconEditOff,
  IconRefresh,
  IconLayoutGrid,
} from '@tabler/icons-react';
import { Button } from '../../../ui';
import { useDashboardStore, selectIsEditMode, selectWidgetCount } from '../../../../stores/dashboardStore';
import { FadeIn } from '../../../animations';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// DashboardToolbar Component
// ============================================================================

export interface DashboardToolbarProps {
  /** Optional title override */
  title?: string;
  /** Whether to show the widget count badge */
  showWidgetCount?: boolean;
  /** Callback when refresh is clicked */
  onRefresh?: () => void;
}

export function DashboardToolbar({
  title = 'Dashboard',
  showWidgetCount = true,
  onRefresh,
}: DashboardToolbarProps) {
  const theme = useMantineTheme();
  const isEditMode = useDashboardStore(selectIsEditMode);
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };
  const widgetCount = useDashboardStore(selectWidgetCount);
  const toggleEditMode = useDashboardStore((state) => state.toggleEditMode);
  const openPicker = useDashboardStore((state) => state.openPicker);
  const resetToDefault = useDashboardStore((state) => state.resetToDefault);

  return (
    <FadeIn>
      <Group
        justify="space-between"
        align="center"
        mb="md"
        px="md"
        py="sm"
        style={{
          backgroundColor: theme.white,
          borderRadius: theme.radius.md,
          border: `1px solid ${theme.colors.gray[2]}`,
        }}
      >
        {/* Left side: Title and widget count */}
        <Group gap="sm">
          <IconLayoutGrid size={24} color={theme.colors.blue[6]} />
          <Text fw={600} size="lg" style={accessibilityStyles}>
            {title}
          </Text>
          {showWidgetCount && (
            <Badge variant="light" size="sm">
              {widgetCount} {widgetCount === 1 ? 'widget' : 'widgets'}
            </Badge>
          )}
        </Group>

        {/* Right side: Actions */}
        <Group gap="xs">
          {/* Global Refresh button - always visible */}
          <Tooltip label="Refresh all widgets" withArrow>
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconRefresh size={16} />}
              onClick={onRefresh}
              disabled={!onRefresh}
            >
              Refresh
            </Button>
          </Tooltip>

          {/* Edit mode toggle */}
          <Tooltip
            label={isEditMode ? 'Exit edit mode' : 'Enter edit mode to rearrange widgets'}
            withArrow
          >
            <Button
              variant={isEditMode ? 'filled' : 'light'}
              size="xs"
              color={isEditMode ? 'blue' : 'gray'}
              leftSection={isEditMode ? <IconEditOff size={16} /> : <IconEdit size={16} />}
              onClick={toggleEditMode}
            >
              {isEditMode ? 'Done' : 'Edit'}
            </Button>
          </Tooltip>

          {/* Add widget button */}
          <Tooltip label="Add a new widget" withArrow>
            <Button
              variant="filled"
              size="xs"
              leftSection={<IconPlus size={16} />}
              onClick={openPicker}
            >
              Add Widget
            </Button>
          </Tooltip>

          {/* Reset button (only in edit mode) */}
          {isEditMode && (
            <Tooltip label="Reset to default layout" withArrow>
              <Button
                variant="subtle"
                size="xs"
                color="red"
                onClick={resetToDefault}
              >
                Reset
              </Button>
            </Tooltip>
          )}
        </Group>
      </Group>
    </FadeIn>
  );
}

// ============================================================================
// Compact Toolbar Variant
// ============================================================================

export interface CompactToolbarProps {
  /** Callback when refresh is clicked */
  onRefresh?: () => void;
}

export function CompactToolbar({ onRefresh }: CompactToolbarProps) {
  const theme = useMantineTheme();
  const isEditMode = useDashboardStore(selectIsEditMode);
  const toggleEditMode = useDashboardStore((state) => state.toggleEditMode);
  const openPicker = useDashboardStore((state) => state.openPicker);

  return (
    <Group gap="xs">
      {onRefresh && (
        <Tooltip label="Refresh" withArrow>
          <Button variant="subtle" size="xs" p={6} onClick={onRefresh}>
            <IconRefresh size={16} />
          </Button>
        </Tooltip>
      )}

      <Tooltip label={isEditMode ? 'Done editing' : 'Edit layout'} withArrow>
        <Button
          variant={isEditMode ? 'filled' : 'subtle'}
          size="xs"
          p={6}
          onClick={toggleEditMode}
        >
          {isEditMode ? <IconEditOff size={16} /> : <IconEdit size={16} />}
        </Button>
      </Tooltip>

      <Tooltip label="Add widget" withArrow>
        <Button variant="subtle" size="xs" p={6} onClick={openPicker}>
          <IconPlus size={16} />
        </Button>
      </Tooltip>
    </Group>
  );
}

export default DashboardToolbar;
